export function getApiErrorMessage(error, fallback = "Não foi possível concluir a operação.") {
    const rawMessage = error?.response?.data?.message;
    const rawError = error?.response?.data?.error;

    const message = Array.isArray(rawMessage)
        ? rawMessage.filter(Boolean).join(" ")
        : rawMessage;

    const finalMessage = message || rawError || fallback;

    return String(finalMessage || fallback).trim() || fallback;
}

function pick(...values) {
    return values.find((value) => value !== undefined && value !== null);
}

export function normalizeTeacher(raw = {}) {
    if (!raw || typeof raw !== "object") return raw;

    return {
        id: pick(raw.id, raw.id_usuario, raw.userId),
        name: pick(raw.name, raw.nome),
        email: raw.email,
        cpf: pick(raw.cpf, raw.cpf_cnpj),
        phone: pick(raw.phone, raw.celular, raw.telefone),
        zipCode: pick(raw.zipCode, raw.cep),
        street: pick(raw.street, raw.endereco, raw.rua),
        number: pick(raw.number, raw.numero),
        complement: pick(raw.complement, raw.complemento),
        district: pick(raw.district, raw.bairro),
        city: pick(raw.city, raw.cidade),
        state: pick(raw.state, raw.uf, raw.estado),
        referralCode: pick(raw.referralCode, raw.indicacao_ref, raw.indicacao),
    };
}

export function normalizeAdmin(raw = {}) {
    if (!raw || typeof raw !== "object") return raw;

    const type = pick(raw.type, raw.role, raw.tipo);

    return {
        adminId: pick(raw.adminId, raw.id, raw.id_adm),
        id: pick(raw.id, raw.adminId, raw.id_adm),
        email: raw.email,
        type,
        role: type,
    };
}

export function normalizePlan(raw = {}) {
    const plan = raw?.plan && typeof raw.plan === "object" ? raw.plan : raw;

    if (!plan || typeof plan !== "object") return raw;

    const customTrialLimit = pick(plan.customTrialLimit, plan.sugestoes_utilizadas_custom);

    return {
        subscriptionId: pick(plan.subscriptionId, plan.id, plan.id_plano),
        id: pick(plan.id, plan.subscriptionId, plan.id_plano),
        userId: pick(plan.userId, plan.id_usuario),
        status: pick(plan.status, plan.subscriptionStatus),
        plan: pick(plan.plan, plan.planName, plan.plano),
        planName: pick(plan.planName, plan.plan, plan.plano),
        externalSubscriptionId: pick(plan.externalSubscriptionId, plan.adesao_id),
        externalCustomerId: pick(plan.externalCustomerId, plan.cliente_id, plan.customerId),
        subscribedAt: pick(plan.subscribedAt, plan.data_adesao),
        canceledAt: pick(plan.canceledAt, plan.data_cancelamento),
        cancelReason: pick(plan.cancelReason, plan.motivo_cancelamento),
        lastEventAt: pick(plan.lastEventAt, plan.data_ultimo_evento),
        usedGeneration: pick(plan.usedGeneration, plan.sugestoes_utilizadas, 0),
        detailsJson: pick(plan.detailsJson, plan.detalhes_json),
        monthReference: pick(plan.monthReference, plan.mes_ref),
        customTrialLimit,
        creditCardToken: pick(plan.creditCardToken, plan.cartao_credito_token),
        creditCardBrand: pick(plan.creditCardBrand, plan.cartao_credito_bandeira),
        creditCardLast4: pick(plan.creditCardLast4, plan.cartao_credito_last4),
        paymentMethod: pick(plan.paymentMethod, plan.payment_method),
        currentPeriodStart: pick(plan.currentPeriodStart, plan.current_period_start),
        currentPeriodEnd: pick(plan.currentPeriodEnd, plan.current_period_end),
        lastPaymentId: pick(plan.lastPaymentId, plan.last_payment_id),
        lastPaymentStatus: pick(plan.lastPaymentStatus, plan.last_payment_status),
        lastPaymentDueDate: pick(plan.lastPaymentDueDate, plan.last_payment_due_date),
        remoteInactivatedAt: pick(plan.remoteInactivatedAt, plan.remote_inactivated_at),
        suspendedAt: pick(plan.suspendedAt, plan.suspended_at),
        pixPayment: pick(plan.pixPayment, plan.pix_payment),
        usedImages: pick(plan.usedImages, plan.imagens_utilizadas, 0),
        extraImageBalance: pick(plan.extraImageBalance, plan.saldo_extra_imagens, 0),
        trialLimit: pick(plan.trialLimit, plan.trial_limit, plan.trialLimitValue, customTrialLimit, 1),
        documentQuota: pick(plan.documentQuota, plan.document_quota),
        imageQuota: pick(plan.imageQuota, plan.image_quota),
    };
}

export function normalizeTeacherForAdmin(raw = {}) {
    if (!raw || typeof raw !== "object") return raw;

    const subscriptionStatus = pick(raw.subscriptionStatus, raw.status);

    return {
        id: pick(raw.id, raw.id_usuario, raw.userId),
        name: pick(raw.name, raw.nome),
        cpf: pick(raw.cpf, raw.cpf_cnpj),
        email: raw.email,
        phone: pick(raw.phone, raw.celular),
        city: pick(raw.city, raw.cidade),
        state: pick(raw.state, raw.uf),
        plan: pick(raw.plan, raw.plano),
        subscriptionStatus,
        status: subscriptionStatus,
        usedGeneration: pick(raw.usedGeneration, raw.sugestoes_utilizadas, 0),
        usedImages: pick(raw.usedImages, raw.imagens_utilizadas, 0),
        extraImageBalance: pick(raw.extraImageBalance, raw.saldo_extra_imagens, 0),
        createdAt: pick(raw.createdAt, raw.data_criacao, raw.data_cadastro),
        subscribedAt: pick(raw.subscribedAt, raw.subscribeAt, raw.data_adesao),
        trialLimit: pick(raw.trialLimit, raw.trial_limit, raw.customTrialLimit, 1),
    };
}

export function normalizePartner(raw = {}) {
    if (!raw || typeof raw !== "object") return raw;

    return {
        partnerId: pick(raw.partnerId, raw.id, raw.id_parceiro),
        id: pick(raw.id, raw.partnerId, raw.id_parceiro),
        name: pick(raw.name, raw.nome),
        email: raw.email,
        phone: pick(raw.phone, raw.celular),
        referralCode: pick(raw.referralCode, raw.indicacao_ref),
        couponDiscountPercentage: pick(raw.couponDiscountPercentage, raw.percentual_desconto_cupom, raw.desconto_cupom, 0),
        status: raw.status,
        createdAt: raw.createdAt,
        updatedAt: raw.updatedAt,
    };
}

export function normalizeReferredUser(raw = {}) {
    if (!raw || typeof raw !== "object") return raw;

    const subscriptionStatus = pick(raw.subscriptionStatus, raw.status);

    return {
        id: pick(raw.id, raw.id_usuario, raw.userId),
        name: pick(raw.name, raw.nome),
        cpf: pick(raw.cpf, raw.cpf_cnpj),
        email: raw.email,
        phone: pick(raw.phone, raw.celular),
        referralCode: pick(raw.referralCode, raw.indicacao_ref),
        partnerName: pick(raw.partnerName, raw.parceiro_nome),
        partnerEmail: pick(raw.partnerEmail, raw.parceiro_email),
        subscriptionStatus,
        status: subscriptionStatus,
        plan: pick(raw.plan, raw.plano),
        city: pick(raw.city, raw.cidade),
        state: pick(raw.state, raw.uf),
        createdAt: pick(raw.createdAt, raw.data_criacao),
    };
}

export function normalizeEvaluation(raw = {}) {
    if (!raw || typeof raw !== "object") return raw;

    return {
        evaluationId: pick(raw.evaluationId, raw.id, raw.id_avaliacao),
        id: pick(raw.id, raw.evaluationId, raw.id_avaliacao),
        userId: pick(raw.userId, raw.id_usuario),
        userName: pick(raw.userName, raw.nome_usuario),
        userEmail: pick(raw.userEmail, raw.email_usuario),
        subscriptionId: pick(raw.subscriptionId, raw.id_plano),
        subscriptionStatus: pick(raw.subscriptionStatus, raw.status_plano),
        currentSubscriptionStatus: pick(raw.currentSubscriptionStatus, raw.status_plano_atual),
        stars: pick(raw.stars, raw.estrelas, 0),
        reasons: pick(raw.reasons, raw.motivos, []),
        description: pick(raw.description, raw.descricao),
        context: pick(raw.context, raw.contexto),
        createdAt: pick(raw.createdAt, raw.criado_em, raw.created_at),
    };
}

export function normalizeContact(raw = {}) {
    if (!raw || typeof raw !== "object") return raw;

    return {
        contactId: pick(raw.contactId, raw.id, raw.id_contato),
        id: pick(raw.id, raw.contactId, raw.id_contato),
        userId: pick(raw.userId, raw.id_usuario),
        contactDate: pick(raw.contactDate, raw.data_contato),
        feedback: raw.feedback,
        createdAt: pick(raw.createdAt, raw.criado_em, raw.created_at),
    };
}
